import { useCallback, useEffect, useState } from 'react';
import { api } from '../lib/api';
import {
  canPromptInstall,
  onInstallAvailabilityChange,
  promptInstall,
} from '../lib/install';
import {
  explainBlocker,
  getBrowserAdvice,
  getInstallGuidance,
  getPlatformInfo,
  type PlatformInfo,
} from '../lib/platform';
import { enablePush, getExistingSubscription } from '../lib/pushClient';
import { BellIcon, ShareIcon, UploadIcon } from './Icons';

/**
 * First-run setup, shown immediately after signing in.
 *
 * Getting a family onto a self-hosted PWA fails at two specific moments:
 * nobody installs it, and nobody turns notifications on. A dismissible banner
 * loses that fight. So this is a guided flow that runs once, states plainly
 * what each step is for, and — critically — ends by proving the whole pipeline
 * works with a real notification rather than declaring success.
 *
 * It is resumable by design. On iOS, installing the app moves the user into a
 * separate storage container, so they sign in again inside the app and land
 * back here with step one already satisfied.
 */

const SNOOZE_KEY = 'family.onboarding.snoozedUntil';
const DONE_KEY = 'family.onboarding.completed';
const SNOOZE_DAYS = 3;

type StepState = 'done' | 'current' | 'pending' | 'blocked';

export function shouldShowOnboarding(): boolean {
  if (localStorage.getItem(DONE_KEY) === 'true') return false;

  const snoozed = Number(localStorage.getItem(SNOOZE_KEY) ?? 0);
  return !(snoozed && Date.now() < snoozed);
}

export function Onboarding({ onClose }: { onClose: () => void }) {
  const [platform, setPlatform] = useState<PlatformInfo>(() => getPlatformInfo());
  const [installable, setInstallable] = useState(() => canPromptInstall());
  const [subscribed, setSubscribed] = useState(false);
  const [tested, setTested] = useState(false);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const guidance = getInstallGuidance(platform);
  const explanation = explainBlocker(platform);
  const browserAdvice = getBrowserAdvice(platform);

  const refresh = useCallback(async () => {
    setPlatform(getPlatformInfo());
    setInstallable(canPromptInstall());
    setSubscribed((await getExistingSubscription()) !== null);
  }, []);

  useEffect(() => {
    void refresh();
    return onInstallAvailabilityChange(() => setInstallable(canPromptInstall()));
  }, [refresh]);

  /**
   * Re-check when the user comes back. They leave to add the app to the Home
   * Screen or to flip a switch in Settings, and the flow should reflect that
   * without anyone having to reload.
   */
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') void refresh();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [refresh]);

  const installDone = platform.isStandalone;
  // Installing is only a hard prerequisite on iOS; elsewhere push works in a
  // normal tab, so we never block someone behind an optional step.
  const installSatisfied = installDone || !guidance.required;

  const finish = () => {
    localStorage.setItem(DONE_KEY, 'true');
    onClose();
  };

  const snooze = () => {
    localStorage.setItem(
      SNOOZE_KEY,
      String(Date.now() + SNOOZE_DAYS * 24 * 60 * 60 * 1000),
    );
    onClose();
  };

  const handleInstall = async () => {
    setBusy(true);
    setError(null);

    const outcome = await promptInstall();
    if (outcome === 'dismissed') {
      setError('Installation was cancelled. You can run it again any time.');
    }

    await refresh();
    setBusy(false);
  };

  const handleEnable = async () => {
    setBusy(true);
    setError(null);

    const result = await enablePush();
    if (!result.ok) {
      setError(result.message ?? 'Notifications could not be turned on.');
    }

    await refresh();
    setBusy(false);
  };

  const handleTest = async () => {
    setBusy(true);
    setError(null);

    try {
      const result = await api.testPush();
      if (result.results.some((entry) => entry.ok)) {
        setTested(true);
      } else {
        setError(
          result.results[0]?.error ??
            'The test could not be delivered. Check that notifications are not muted in your device settings.',
        );
      }
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'The test could not be sent.');
    }

    setBusy(false);
  };

  const steps: Array<{
    key: string;
    label: string;
    icon: React.ReactNode;
    state: StepState;
    body: React.ReactNode;
  }> = [
    {
      key: 'install',
      label: guidance.title,
      icon: <ShareIcon className="" />,
      state: installDone ? 'done' : 'current',
      body: installDone ? (
        <p>Done — you are running the installed app.</p>
      ) : platform.blocker === 'ios-wrong-browser' ? (
        <>
          <p>{explanation.detail}</p>
          {explanation.steps && (
            <ol className="steps">
              {explanation.steps.map((step) => (
                <li key={step}>{step}</li>
              ))}
            </ol>
          )}
        </>
      ) : (
        <>
          <p>{guidance.detail}</p>
          {/*
            Never offer a one-tap install on iOS. Safari has no install
            prompt, so the only correct guidance there is the manual Share ->
            Add to Home Screen steps; a button would be a dead end.
          */}
          {installable && !platform.isIOS ? (
            <button
              type="button"
              className="btn btn-primary btn-block"
              onClick={() => void handleInstall()}
              disabled={busy}
              style={{ marginTop: 12 }}
            >
              <UploadIcon className="" />
              Install Family
            </button>
          ) : (
            <ol className="steps">
              {guidance.steps.map((step) => (
                <li key={step}>{step}</li>
              ))}
            </ol>
          )}
          {/*
            No separate "you'll be signed out" note here: on the platforms
            where that happens (iOS) the manual steps above already say it, in
            the place it actually occurs. Saying it twice reads as a warning
            about two different things.
          */}
        </>
      ),
    },
    {
      key: 'notify',
      label: 'Turn on notifications',
      icon: <BellIcon className="" />,
      state: subscribed
        ? 'done'
        : !installSatisfied
          ? 'blocked'
          : platform.canEnablePush
            ? 'current'
            : 'blocked',
      body: subscribed ? (
        <p>Done — this device is registered for notifications.</p>
      ) : !installSatisfied ? (
        <p>Available once the app is on your Home Screen.</p>
      ) : platform.canEnablePush ? (
        <>
          <p>
            So you hear about new photos and messages without having to go looking. Your
            device will ask you to allow it.
          </p>
          <button
            type="button"
            className="btn btn-primary btn-block"
            onClick={() => void handleEnable()}
            disabled={busy}
            style={{ marginTop: 12 }}
          >
            {busy ? 'Setting up…' : 'Turn on notifications'}
          </button>
        </>
      ) : (
        <>
          <p>{explanation.detail}</p>
          {explanation.steps && (
            <ol className="steps">
              {explanation.steps.map((step) => (
                <li key={step}>{step}</li>
              ))}
            </ol>
          )}
        </>
      ),
    },
    {
      key: 'test',
      label: 'Check that it works',
      icon: <BellIcon className="" />,
      state: tested ? 'done' : subscribed ? 'current' : 'pending',
      body: tested ? (
        <p>A test notification was delivered. Everything is working.</p>
      ) : subscribed ? (
        <>
          <p>
            Send yourself one real notification, so you know it works rather than hoping
            it does.
          </p>
          <button
            type="button"
            className="btn btn-primary btn-block"
            onClick={() => void handleTest()}
            disabled={busy}
            style={{ marginTop: 12 }}
          >
            {busy ? 'Sending…' : 'Send me a test notification'}
          </button>
        </>
      ) : (
        <p>Available once notifications are on.</p>
      ),
    },
  ];

  const allDone = installSatisfied && subscribed && tested;

  return (
    <div className="onboarding" role="dialog" aria-modal="true" aria-label="Set up Family">
      <div className="onboarding-sheet">
        <header className="onboarding-header">
          <h1>Let’s get you set up</h1>
          <p>Two quick things, then you are done for good on this device.</p>
        </header>

        {/*
          Shown before the steps, not after: if this browser cannot deliver
          notifications dependably, switching browsers should happen before
          someone spends time installing and configuring the wrong copy.
        */}
        {browserAdvice && (
          <div className="banner attention" style={{ marginBottom: 20 }}>
            <h3>{browserAdvice.title}</h3>
            <p>{browserAdvice.detail}</p>
            {browserAdvice.steps && (
              <ol className="steps">
                {browserAdvice.steps.map((step) => (
                  <li key={step}>{step}</li>
                ))}
              </ol>
            )}
          </div>
        )}

        <div className="onboarding-steps">
          {steps.map((step, index) => (
            <section key={step.key} className={`onboarding-step is-${step.state}`}>
              <div className="onboarding-step-marker" aria-hidden="true">
                {step.state === 'done' ? (
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={3}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M20 6 9 17l-5-5" />
                  </svg>
                ) : (
                  <span>{index + 1}</span>
                )}
              </div>
              <div className="onboarding-step-content">
                <h2>{step.label}</h2>
                {step.body}
              </div>
            </section>
          ))}
        </div>

        {error && (
          <div className="banner error" style={{ marginTop: 4 }}>
            <p>{error}</p>
          </div>
        )}

        <footer className="onboarding-footer">
          {allDone ? (
            <button type="button" className="btn btn-primary btn-block" onClick={finish}>
              Start using Family
            </button>
          ) : (
            <>
              <button type="button" className="btn btn-block" onClick={snooze}>
                Skip for now
              </button>
              <p className="onboarding-note">
                You can finish this any time from Settings.
              </p>
            </>
          )}
        </footer>
      </div>
    </div>
  );
}
